import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { getRoot } from 'mobx-easy';
import { Provider } from 'mobx-react';
import { MemoryRouter } from 'react-router';
import ReactDOMServer from 'react-dom/server';
import { Task } from '@bringg/types';

import RootStore from 'bringg-web/stores/root-store';
import TaskTooltipFooter from './task-tooltip-footer/task-tooltip-footer';
import TasksTooltipContent from './task-tooltip-content';
import useRouteMonitoringStore from '../../../../routes-monitoring-screen/stores/helpers/use-route-monitoring-store';

import styles from '../tooltip.module.scss';

interface Props {
	tasks: Task[];
	multiSelect: boolean;
	setMultiSelect?: React.Dispatch<React.SetStateAction<boolean>>;
	redrewTooltip?: () => void;
	dataTestId: string;
}

const TaskTooltip = ({ tasks, dataTestId, redrewTooltip, setMultiSelect, multiSelect }: Props) => {
	const {
		selectedTasksStore: { selectedTasks, isTaskSelected, setSelectedTasks }
	} = useRouteMonitoringStore();
	const [taskIndex, setTaskIndex] = useState<number>(
		(selectedTasks?.length === 1 && tasks.findIndex(task => isTaskSelected(task.id))) || 0
	);

	useEffect(() => {
		redrewTooltip?.();
	}, [multiSelect]);

	return (
		<div className={classNames(styles.monitoringTooltip)}>
			{!multiSelect && <TasksTooltipContent task={tasks[taskIndex]} dataTestId={dataTestId} />}
			{!multiSelect && tasks.length > 1 && <div className={styles.divider} />}

			{tasks.length > 1 && (
				<TaskTooltipFooter
					tasks={tasks}
					currentTaskIndex={taskIndex}
					tasksLength={tasks.length}
					toggleTask={(_taskIndex, event, isBreak) => {
						const ctrlKey = event.metaKey || event.ctrlKey;

						if (isBreak && ctrlKey) {
							setTaskIndex(_taskIndex);
							return;
						}

						setMultiSelect?.(ctrlKey);

						if (multiSelect || ctrlKey || _taskIndex !== taskIndex) {
							setTaskIndex(_taskIndex);
							setSelectedTasks?.(tasks[_taskIndex], ctrlKey);
						}
					}}
				/>
			)}
		</div>
	);
};

export const taskTooltipProvider = (task: Task): string => {
	return ReactDOMServer.renderToString(
		<MemoryRouter initialEntries={[``]}>
			<Provider {...getRoot<RootStore>().data}>
				<div className={classNames(styles.monitoringTooltip, styles.mapTooltip)}>
					<TasksTooltipContent task={task} isMapTooltip dataTestId="map-task-tooltip" />
				</div>
			</Provider>
		</MemoryRouter>
	);
};

export default TaskTooltip;
